import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Shield, ShieldCheck, Smartphone, Copy, Check, Loader2 } from 'lucide-react';

/** Security page — enable / disable Google Authenticator 2FA. */
export default function TwoFactorSetup() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [setupData, setSetupData] = useState(null); // {qr_png_b64, secret, provisioning_uri}
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadStatus = async () => {
    try { const r = await api.get('/auth/2fa/status'); setStatus(r.data); }
    catch { /* silent */ }
  };
  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post('/auth/2fa/setup');
      setSetupData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to start 2FA setup'); }
    finally { setBusy(false); }
  };

  const verifyEnable = async (e) => {
    e.preventDefault();
    if (code.replace(/\D/g, '').length !== 6) { toast.error('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      await api.post('/auth/2fa/verify', { code });
      toast.success('2FA enabled — you\'ll need your authenticator on every future login');
      setSetupData(null); setCode('');
      await loadStatus();
      if (refresh) await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Invalid code'); }
    finally { setBusy(false); }
  };

  const copySecret = () => {
    if (!setupData?.secret) return;
    navigator.clipboard.writeText(setupData.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isEnabled = status?.enabled;
  const isForced = user?.require_2fa_setup;

  return (
    <div className="py-6 max-w-3xl mx-auto space-y-6" data-testid="2fa-setup-page">
      {isForced && !isEnabled && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3" data-testid="2fa-required-banner">
          <ShieldCheck className="text-amber-600 shrink-0 mt-0.5" size={18}/>
          <div className="text-sm text-amber-900">
            <b>2FA is required by your workspace.</b> Please finish enrolment below to continue. You&apos;ll be blocked from the rest of the app until this is set up.
          </div>
        </div>
      )}
      <div>
        <div className="overline">Security</div>
        <h1 className="font-serif text-4xl">Two-factor authentication</h1>
        <p className="text-slate-600 mt-2 text-sm">
          Protect your account with a one-time code from Google Authenticator (or any TOTP app).
        </p>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass p-8">
        <div className="flex items-start gap-4 mb-6">
          <div className={`w-14 h-14 rounded-2xl grid place-items-center ${isEnabled ? 'bg-emerald-100' : 'bg-slate-100'}`}>
            {isEnabled ? <ShieldCheck className="text-emerald-600" size={26}/> : <Shield className="text-slate-500" size={26}/>}
          </div>
          <div className="flex-1">
            <div className="font-serif text-xl">{isEnabled ? '2FA is enabled' : '2FA is not enabled'}</div>
            <div className="text-sm text-slate-500 mt-1">
              {isEnabled
                ? `Signed in as ${user?.name}. Codes come from your authenticator app.`
                : 'Anyone with your email + password can currently sign in. Enable 2FA below to require a rotating 6-digit code.'}
            </div>
          </div>
        </div>

        {!isEnabled && !setupData && (
          <button onClick={startSetup} disabled={busy}
            data-testid="2fa-start-setup-btn"
            className="px-6 py-2.5 rounded-full bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-60 inline-flex items-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <Shield size={14}/>}
            Enable 2FA
          </button>
        )}

        {!isEnabled && setupData && (
          <div className="space-y-6 mt-4 p-6 rounded-2xl bg-slate-50 border border-slate-200">
            <div className="flex items-start gap-3">
              <Smartphone size={18} className="text-slate-500 shrink-0 mt-0.5"/>
              <div className="text-xs text-slate-700 leading-relaxed">
                <b>Step 1</b>: Open Google Authenticator (or Authy / 1Password / Microsoft Authenticator) on your phone → tap <b>+</b> → <b>Scan a QR code</b> → point it at the code below.
              </div>
            </div>

            <div className="flex justify-center">
              <img src={`data:image/png;base64,${setupData.qr_png_b64}`}
                alt="2FA QR code" data-testid="2fa-qr-image"
                className="w-52 h-52 rounded-xl border-2 border-white shadow-lg bg-white p-2"/>
            </div>

            <div>
              <div className="text-xs text-slate-500 mb-1">Can&apos;t scan? Enter this secret manually:</div>
              <div className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 p-3 font-mono text-sm">
                <div className="flex-1 tracking-widest">{setupData.secret}</div>
                <button onClick={copySecret} className="p-1 hover:bg-slate-100 rounded" data-testid="2fa-copy-secret">
                  {copied ? <Check size={14} className="text-emerald-600"/> : <Copy size={14}/>}
                </button>
              </div>
            </div>

            <form onSubmit={verifyEnable} className="pt-2">
              <div className="text-xs text-slate-700 mb-2"><b>Step 2</b>: Enter the 6-digit code shown in your app for <b>Iris Realty</b>:</div>
              <div className="flex gap-2">
                <input type="text" inputMode="numeric" maxLength={6}
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  data-testid="2fa-verify-code-input"
                  className="flex-1 text-center tracking-[0.5em] font-serif text-xl px-4 py-3 rounded-lg border-2 border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#679286]/40"/>
                <button type="submit" disabled={busy || code.length !== 6}
                  data-testid="2fa-verify-btn"
                  className="px-6 py-3 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2">
                  {busy && <Loader2 size={14} className="animate-spin"/>}
                  Verify
                </button>
              </div>
            </form>
          </div>
        )}

        {isEnabled && (
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-xs text-emerald-900 leading-relaxed">
            To disable or reset 2FA (e.g. lost phone), ask another workspace owner to reset it from Team → your row → Actions → <b>Reset 2FA</b>.
          </div>
        )}
      </motion.div>
    </div>
  );
}
